// Building/SurfaceMaterials_Shared.ts — Buildings Set1 §6.2/§7: shared helpers for mapping a
// `MeshPlan`'s SEMANTIC material slots onto per-slot recipe ids and cloning per-mesh
// `ShaderMaterial` arrays aligned with a geometry's `groups`. Both `Building3D` (3D) and the 2D
// floor-material overlay (`PlanOverlays2D`) consume these.
import {SurfaceMaterialManager} from "../../../../../Building/Materials/SurfaceMaterialManager.js";
import type {ResolvedRoomMaterials} from "../../../../../Server/DB/mapObjects/Building/BuildingGeometry.js";
import {BUILDING_MATERIAL_SLOTS} from "../../../../../Building/Geometry/BuildBuildingMeshPlans.js";
/** The 3D-visible BUILDING material slots (the wall/floor/opening layer; roof is handled
 *  separately via its own slot set). Maps a MeshPlan group's `materialSlot` → recipe id.
 *  `windowGlass` is the special transparent slot (separate pass). */
export const BUILDING_OPAQUE_SLOT_MAP: Record<string, keyof ResolvedRoomMaterials> = {
	[BUILDING_MATERIAL_SLOTS.floorTop]: "floorTop",
	[BUILDING_MATERIAL_SLOTS.floorUnderside]: "floorUnderside",
	[BUILDING_MATERIAL_SLOTS.floorEdge]: "floorEdge",
	[BUILDING_MATERIAL_SLOTS.ceiling]: "ceiling",
	[BUILDING_MATERIAL_SLOTS.wallExterior]: "wallExterior",
	[BUILDING_MATERIAL_SLOTS.wallInterior]: "wallInterior",
	[BUILDING_MATERIAL_SLOTS.wallTop]: "wallTop",
	[BUILDING_MATERIAL_SLOTS.wallReveal]: "wallReveal",
	[BUILDING_MATERIAL_SLOTS.doorFrame]: "doorFrame",
	[BUILDING_MATERIAL_SLOTS.doorSlab]: "doorSlab",
	[BUILDING_MATERIAL_SLOTS.doorHardware]: "doorHardware",
	[BUILDING_MATERIAL_SLOTS.windowFrame]: "windowFrame",
};

/** The transparent BUILDING slots (rendered as a distinct glass pass). */
export const BUILDING_TRANSPARENT_SLOT_MAP: Record<string, keyof ResolvedRoomMaterials> = {
	[BUILDING_MATERIAL_SLOTS.windowGlass]: "windowGlass",
};

/** The roof material slots → recipe-id key on `ResolvedRoomMaterials` (roofSurface/roofFascia/
 *  roofSoffit). */
export const ROOF_SLOT_MAP: Record<string, keyof ResolvedRoomMaterials> = {
	roofSurface: "roofSurface",
	roofFascia: "roofFascia",
	roofSoffit: "roofSoffit",
};

/** Group a MeshPlan's semantic-slot groups by slot name (an array of {start, count} per slot) —
 *  so the renderer can build a material ARRAY aligned with `geometry.groups` (one material per
 *  group, indexed by `group.materialIndex`), mapping each group's slot → the room's recipe id. */
export function GroupIndicesBySlot(groups: {materialSlot: string; start: number; count: number}[]): Map<string, Array<{start: number; count: number}>> {
	const out = new Map<string, Array<{start: number; count: number}>>();
	for (const g of groups) {
		const arr = out.get(g.materialSlot) ?? [];
		arr.push({start: g.start, count: g.count});
		out.set(g.materialSlot, arr);
	}
	return out;
}

/** The resolved recipe id for a semantic slot on a room, or a fallback id.
 *  `slotMap` maps a MeshPlan group's semantic slot string → a key of `ResolvedRoomMaterials`; the
 *  value returned by indexing the map may be a `symbol` only if the key type admits it, so we
 *  narrow via a string-keyed lookup of the material map. */
export function SlotRecipeID(slotMap: {[k: string]: keyof ResolvedRoomMaterials | undefined}, slot: string, materials: ResolvedRoomMaterials, fallback = "concrete"): string {
	const key = slotMap[slot];
	if (key == null) return fallback;
	const id = (materials as unknown as Record<string, string>)[key as string];
	return id && id != "" ? id : fallback;
}

/** A material-array cell for one geometry group: the recipe id + whether it is the transparent
 *  (glass) slot. The renderer resolves each cell to a cloned ShaderMaterial. */
export type MaterialArrayCell = {slot: string; recipeID: string; transparent: boolean};

/** Build the per-group material-array cells for a MeshPlan (one cell PER GROUP, so the renderer
 *  can set `geometry.groups[i].materialIndex = i` and hand a material array to `mesh.material`).
 *  `groups` is the MeshPlan's group list (already slot-tagged). */
export function BuildMaterialArrayCells(
	groups: {materialSlot: string; start: number; count: number}[],
	materials: ResolvedRoomMaterials,
	opaqueSlotMap: Record<string, keyof ResolvedRoomMaterials> = BUILDING_OPAQUE_SLOT_MAP,
	transparentSlotMap: Record<string, keyof ResolvedRoomMaterials> = BUILDING_TRANSPARENT_SLOT_MAP,
): MaterialArrayCell[] {
	return groups.map(g=>{
		if (transparentSlotMap[g.materialSlot] != null) {
			return {slot: g.materialSlot, recipeID: SlotRecipeID(transparentSlotMap, g.materialSlot, materials), transparent: true};
		}
		return {slot: g.materialSlot, recipeID: SlotRecipeID(opaqueSlotMap, g.materialSlot, materials), transparent: false};
	});
}

/** P7 — collect the DISTINCT recipe ids a `RoomMaterials` + roof resolve to across the OPAQUE +
 *  transparent building material slots (plus the roof surface/fascia/soffit entries, which live
 *  on the same map). This is what the P7 program pre-warm (`SurfaceMaterialManager.warmUpPrograms`)
 *  compiles ahead of the first appearance, so walking up to a building pays no shader-compile
 *  hitch. `Building3D` schedules the warm-up from this once per mounted building. */
export function CollectBuildingMaterialRecipeIDs(materials: ResolvedRoomMaterials): string[] {
	const out: string[] = [];
	// The building-surface slots (both the opaque set and the transparent glass — every slot on
	// `materials` that the §6.2 slot maps can reference, deduped).
	const slotMaps: Record<string, keyof ResolvedRoomMaterials>[] = [BUILDING_OPAQUE_SLOT_MAP, BUILDING_TRANSPARENT_SLOT_MAP, ROOF_SLOT_MAP];
	const add = (id: string): void => { if (id && id != "" && !out.includes(id)) out.push(id); };
	for (const map of slotMaps) {
		for (const key of Object.values(map)) {
			add((materials as unknown as Record<string, string>)[key as string]);
		}
	}
	return out;
}
